import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import { Button, majorScale, Menu, minorScale } from '.';
import { CompanySelect } from '../components/companies/CompanySelect';
import { IPaginationFilter } from '../stores/utils/modelWithCRUD';
import { InputGroup } from './InputGroup';
import { MenuItem } from './Menu';
import { Popover } from './Popover';
import { Select } from './Select';
import { TagInput } from './TagInput';
import { UserSelect } from './UserSelect';

export interface IFilter<S extends any> {
  field: keyof S | null;
  operand:
    | '' // Equals
    | '__neq'
    | '__contains'
    | '__isempty'
    | '__startswith'
    | '__endswith'
    | '__lte'
    | '__gte'
    | '__in'
    | '__nin';
  value?: string | number;
}

export interface IFilterOption<S extends { id: any }> {
  type: 'string' | 'number' | 'option' | 'date' | 'company' | 'tag' | 'user';
  field: keyof S | string;
  label: string;
  options?: { id: string; label: string }[];
}

export interface IFilterPopoverProps<S extends { id: any }> {
  filterFields: IFilterOption<S>[];
  filter: IPaginationFilter<S>['filter'] | undefined;
  onChange: (filters: IPaginationFilter<S>['filter']) => void;
}

export const FilterPopover = <S extends { id: any }>({
  filterFields,
  onChange,
  filter,
}: IFilterPopoverProps<S>) => {
  const intl = useIntl();

  useEffect(() => {
    setFilters(
      Object.keys(filter || {})
        .filter(
          (key) =>
            !!filter[key] && filterFields.find((field) => field.field === key)
        )
        .map((key) => ({
          field: key.split('__')[0] as any,
          operand:
            key.indexOf('__') > -1
              ? (('__' + key.split('__').pop()) as any)
              : '',
          value: filter[key],
        }))
    );
  }, [filter, filterFields]);

  const [filters, setFilters] = useState<IFilter<S>[]>([]);

  const addFilter = () => {
    setFilters((f) => {
      const filters = Array.from(f);
      filters.push({
        field: null,
        operand: '',
        value: '',
      });
      return filters;
    });
  };

  const removeFilter = (index: number) => {
    setFilters((f) => {
      const filters = Array.from(f);
      filters.splice(index, 1);
      return filters;
    });
  };

  const updateFilter = (filter: IFilter<S>, index: number) => {
    if (fieldType(filter.field) === 'tag') {
      filter.operand = '__in';
    }
    setFilters((f) => {
      const filters = Array.from(f);
      filters.splice(index, 1, filter);
      return filters;
    });
  };

  const generalOperands: { id: IFilter<S>['operand']; label: string }[] = [
    {
      id: '',
      label: intl.formatMessage({
        defaultMessage: 'Equals',
      }),
    },
    {
      id: '__neq',
      label: intl.formatMessage({
        defaultMessage: 'Does not equal',
      }),
    },
  ];

  const tagOperands: typeof generalOperands = [
    {
      id: '__in',
      label: intl.formatMessage({
        defaultMessage: 'One of',
      }),
    },
    {
      id: '__nin',
      label: intl.formatMessage({
        defaultMessage: 'Not one of',
      }),
    },
  ];

  const numberOperands: typeof generalOperands = [
    ...generalOperands,
    {
      id: '__lte',
      label: intl.formatMessage({
        defaultMessage: 'Less than',
      }),
    },
    {
      id: '__gte',
      label: intl.formatMessage({
        defaultMessage: 'Greater than',
      }),
    },
  ];

  const stringOperands: typeof generalOperands = [
    ...generalOperands,
    {
      id: '__contains',
      label: intl.formatMessage({
        defaultMessage: 'Contains',
      }),
    },
    {
      id: '__startswith',
      label: intl.formatMessage({
        defaultMessage: 'Starts with',
      }),
    },
    {
      id: '__endswith',
      label: intl.formatMessage({
        defaultMessage: 'Ends with',
      }),
    },
  ];

  const buildFilter = () => {
    const filter: IPaginationFilter<S>['filter'] = {};
    filters
      .filter((f) => !!f.field)
      .forEach((f) => {
        filter![f.field + f.operand] = f.value;
      });
    onChange(filter);
  };

  const fieldType = (field: keyof S | null): IFilterOption<S>['type'] => {
    const found = filterFields.find((f) => f.field === field);
    if (found) {
      return found.type;
    }
    return 'string';
  };

  const fieldForFilter = (
    field: keyof S | null
  ): IFilterOption<S> | undefined =>
    filterFields.find((f) => f.field === field);

  return (
    <Popover
      onClose={() => buildFilter()}
      content={() => (
        <Menu minWidth={360}>
          {filters.map((filter, index) => (
            <Box
              key={`${filter.field}-${filter.operand}-${index}`}
              display="flex"
              paddingX={majorScale(2)}
              paddingY={minorScale(1)}
              alignItems="center"
            >
              <Button
                minimal
                iconRight="remove"
                marginLeft={majorScale(-1)}
                onClick={(e: any) => {
                  e.stopPropagation();
                  removeFilter(index);
                }}
              />
              <Box
                is="p"
                whiteSpace="nowrap"
                className="muted"
                width="48px"
                textAlign="right"
              >
                {index === 0
                  ? intl.formatMessage({
                      defaultMessage: 'Where',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'and',
                      description: 'Lowercase AND',
                    })}
              </Box>
              <Box
                marginLeft={majorScale(1)}
                marginTop="4px"
                display="grid"
                gridTemplateColumns="auto auto 1fr"
                width="100%"
                alignItems="center"
              >
                <Select
                  small
                  minimal
                  items={filterFields.map((field, i) => ({
                    id: field.field as string | number,
                    label: field.label,
                  }))}
                  justifyContent="flex-end"
                  valueRenderer={(item) => item.label}
                  itemRenderer={(item) => item.label}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Choose field',
                  })}
                  value={filter.field as string | null}
                  filterItems={(i, q) =>
                    i.label.toLowerCase().indexOf(q.toLowerCase()) > -1
                  }
                  onSelect={(item) =>
                    updateFilter({ ...filter, field: item.id as any }, index)
                  }
                />
                <Select
                  justifyContent="flex-end"
                  small
                  value={filter.operand}
                  minimal
                  items={
                    fieldType(filter.field) === 'string'
                      ? stringOperands
                      : fieldType(filter.field) === 'number'
                      ? numberOperands
                      : fieldType(filter.field) === 'tag'
                      ? tagOperands
                      : generalOperands
                  }
                  valueRenderer={(item) => item.label}
                  itemRenderer={(item) => item.label}
                  filterItems={(i, q) =>
                    i.label.toLowerCase().indexOf(q.toLowerCase()) > -1
                  }
                  onSelect={(item) =>
                    updateFilter({ ...filter, operand: item.id as any }, index)
                  }
                />
                {fieldType(filter.field) === 'company' ? (
                  <CompanySelect
                    small
                    selected={filter.value}
                    onSelect={(company) =>
                      updateFilter({ ...filter, value: company.id }, index)
                    }
                  />
                ) : fieldType(filter.field) === 'user' ? (
                  <UserSelect
                    small
                    selected={filter.value}
                    onSelect={(member) =>
                      updateFilter({ ...filter, value: member.id }, index)
                    }
                  />
                ) : fieldType(filter.field) === 'tag' ? (
                  <TagInput
                    onChange={(tags) =>
                      updateFilter({ ...filter, value: tags.join(',') }, index)
                    }
                    value={filter.value ? (filter.value + '').split(',') : []}
                  />
                ) : fieldType(filter.field) === 'option' ? (
                  <Select
                    small
                    items={
                      fieldForFilter(filter.field)
                        ? fieldForFilter(filter.field)!.options
                        : []
                    }
                    itemRenderer={(item) => item.label}
                    valueRenderer={(item) => item.label}
                    value={filter.value as string | null | undefined}
                    onSelect={(item) =>
                      updateFilter({ ...filter, value: item.id }, index)
                    }
                  />
                ) : (
                  <InputGroup
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Enter value',
                    })}
                    height={24}
                    value={filter.value}
                    onChange={(e) =>
                      updateFilter({ ...filter, value: e.target.value }, index)
                    }
                  />
                )}
              </Box>
            </Box>
          ))}
          <MenuItem
            icon="add"
            text={intl.formatMessage({
              defaultMessage: 'Add filter',
            })}
            onClick={() => addFilter()}
          />
        </Menu>
      )}
    >
      <Button
        iconRight={filters.length > 0 ? 'filter-keep' : 'filter'}
        minimal={true}
        intent={filters.length > 0 ? 'info' : 'none'}
      >
        <FormattedMessage
          defaultMessage="{count, plural, =0 {Add filter} one {# filter} other {# filters}}"
          values={{ count: filters.length }}
        />
      </Button>
    </Popover>
  );
};
